import type { FieldConfig } from './types';
import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import { Icon } from '#const';
import { getIconUrl } from '#utils';
import { DEFAULT_CTA, DEFAULT_FIELDS, DEFAULT_RESULTS, DEFAULT_SCALING_FACTOR } from './logic';

const SettlementCalculator = dynamic(() => import('./SettlementCalculator'));

export const defaultOptions = {
	eyebrow: 'Depo-Provera · MDL Case Estimator',
	title: 'What Could Your Case Be Worth?',
	subtitle: 'Enter your estimated damages below to see your projected settlement range.',
	medical: DEFAULT_FIELDS.medical,
	wages: DEFAULT_FIELDS.wages,
	future: DEFAULT_FIELDS.future,
	scalingFactor: DEFAULT_SCALING_FACTOR,
	results: DEFAULT_RESULTS,
	cta: DEFAULT_CTA,
	showCta: true,
	disclaimerLabel: 'Legal notice',
	disclaimer:
		'Figures are illustrative estimates only and do not constitute legal advice. Actual outcomes vary by jurisdiction, insurance policy limits, comparative fault, evidence strength, and litigation strategy. Mass tort claim values can differ substantially from standard personal injury cases. Consult a licensed attorney for a case-specific evaluation.',
} as const;

/** Builds the grouped object input for one damages field. Defaults live on the
 * subfields (not the object) — matching the repo's object-input convention, which
 * is what makes the values surface pre-filled in the Builder editor. */
function fieldInput(name: string, friendlyName: string, cfg: FieldConfig) {
	return {
		name,
		friendlyName,
		type: 'object',
		defaultValue: { ...cfg },
		subFields: [
			{ name: 'label', friendlyName: 'Label', type: 'string', defaultValue: cfg.label },
			{
				name: 'defaultValue',
				friendlyName: 'Starting Value',
				type: 'number',
				defaultValue: cfg.defaultValue,
			},
			{ name: 'max', friendlyName: 'Maximum', type: 'number', defaultValue: cfg.max },
			{
				name: 'step',
				friendlyName: 'Step',
				type: 'number',
				defaultValue: cfg.step,
				helperText: 'Amount each +/- press adjusts the value.',
			},
		],
	};
}

Builder.registerComponent(SettlementCalculator, {
	name: 'SettlementCalculator',
	friendlyName: 'Settlement Calculator',
	inputs: [
		{
			name: 'eyebrow',
			friendlyName: 'Eyebrow',
			type: 'string',
			defaultValue: defaultOptions.eyebrow,
		},
		{ name: 'title', friendlyName: 'Title', type: 'string', defaultValue: defaultOptions.title },
		{
			name: 'subtitle',
			friendlyName: 'Subtitle',
			type: 'longText',
			defaultValue: defaultOptions.subtitle,
		},
		fieldInput('medical', 'Medical Field', defaultOptions.medical),
		fieldInput('wages', 'Lost Wages Field', defaultOptions.wages),
		fieldInput('future', 'Future Medical Field', defaultOptions.future),
		{
			name: 'scalingFactor',
			friendlyName: 'Scaling Factor',
			type: 'number',
			defaultValue: defaultOptions.scalingFactor,
			min: 0.1,
			max: 10,
			step: 0.1,
			helperText:
				'Tunes the whole estimate up or down. The low (×4) / high (×7) multipliers are fixed in code; this scales their result.',
		},
		{
			name: 'resultsContent',
			friendlyName: 'Results',
			type: 'object',
			defaultValue: { ...defaultOptions.results },
			subFields: [
				{
					name: 'label',
					friendlyName: 'Results Label',
					type: 'string',
					defaultValue: defaultOptions.results.label,
				},
				{
					name: 'lowTag',
					friendlyName: 'Low Result Tag',
					type: 'string',
					defaultValue: defaultOptions.results.lowTag,
				},
				{
					name: 'lowDescription',
					friendlyName: 'Low Result Description',
					type: 'string',
					defaultValue: defaultOptions.results.lowDescription,
				},
				{
					name: 'highTag',
					friendlyName: 'High Result Tag',
					type: 'string',
					defaultValue: defaultOptions.results.highTag,
				},
				{
					name: 'highDescription',
					friendlyName: 'High Result Description',
					type: 'string',
					defaultValue: defaultOptions.results.highDescription,
				},
				{
					name: 'severityLabel',
					friendlyName: 'Severity Label',
					type: 'string',
					defaultValue: defaultOptions.results.severityLabel,
				},
			],
		},
		{
			name: 'showCta',
			friendlyName: 'Show CTA',
			type: 'boolean',
			defaultValue: defaultOptions.showCta,
			helperText: 'Turn off when the surrounding page owns the call-to-action.',
		},
		{
			name: 'cta',
			friendlyName: 'CTA',
			type: 'object',
			defaultValue: { ...defaultOptions.cta },
			subFields: [
				{
					name: 'headline',
					friendlyName: 'Headline',
					type: 'string',
					defaultValue: defaultOptions.cta.headline,
				},
				{
					name: 'meta',
					friendlyName: 'Meta',
					type: 'string',
					defaultValue: defaultOptions.cta.meta,
				},
				{
					name: 'buttonText',
					friendlyName: 'Button Text',
					type: 'string',
					defaultValue: defaultOptions.cta.buttonText,
				},
				{
					name: 'buttonHref',
					friendlyName: 'Button Link',
					type: 'string',
					defaultValue: defaultOptions.cta.buttonHref,
					helperText: 'Anchor or URL the button points to (e.g. #form-container).',
				},
				{
					name: 'subtext',
					friendlyName: 'Subtext',
					type: 'string',
					defaultValue: defaultOptions.cta.subtext,
				},
			],
		},
		{
			name: 'disclaimerLabel',
			friendlyName: 'Disclaimer Label',
			type: 'string',
			defaultValue: defaultOptions.disclaimerLabel,
		},
		{
			name: 'disclaimer',
			friendlyName: 'Disclaimer',
			type: 'longText',
			defaultValue: defaultOptions.disclaimer,
		},
	],
	image: getIconUrl(Icon.DEFAULT),
});
